import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classnames from 'classnames';
import { Link } from 'gatsby';
import Social from 'components/Social/Social';
import icon from 'components/image/headerIcon';
import { Language } from 'enums/types';
import { routes } from 'languages';
import { isEdge, isIE, isSafari } from '../../browserHelper';
import * as style from './footer.module.scss';

interface MenuLink {
	to?: string;
	externalLink?: string;
	name: string;
}

const translations = defineMessages({
	personalTitle: {
		id: 'navigation.personal',
		defaultMessage: 'Personal',
	},
	businessTitle: {
		id: 'navigation.business',
		defaultMessage: 'Business',
	},
	pricingTitle: {
		id: 'navigation.pricing',
		defaultMessage: 'Pricing',
	},
	teamTitle: {
		id: 'navigation.team',
		defaultMessage: 'Team',
	},
	contactTitle: {
		id: 'navigation.contact',
		defaultMessage: 'Contact',
	},
	joinUsTitle: {
		id: 'navigation.joinUs',
		defaultMessage: 'Join us',
	},
	legalTitle: {
		id: 'nav.legal',
		defaultMessage: 'Legal',
	},
	blogTitle: {
		id: 'nav.blog',
		defaultMessage: 'Blog',
	},
	aboutTitle: {
		id: 'nav.aboutTitle',
		defaultMessage: 'About',
	},
	ecosystemTitle: {
		id: 'nav.ecosystem',
		defaultMessage: 'Ecosystem',
	},
	companyTitle: {
		id: 'nav.company',
		defaultMessage: 'Company',
	},
	solutionsTitle: {
		id: 'nav.solutions',
		defaultMessage: 'Solutions',
	},
	faqTitle: {
		id: 'nav.faq',
		defaultMessage: 'FAQ',
	},
	trustCenter: {
		id: 'nav.trustCenter',
		defaultMessage: 'Trust Center',
	},
	community: {
		id: 'navigation.community',
		defaultMessage: 'Community',
	},
	bugBounty: {
		id: 'nav.bugBounty',
		defaultMessage: 'Bug Bounty',
	},
	events: {
		id: 'nav.events',
		defaultMessage: 'Events',
	},
	logoImageDescription: {
		id: 'linkDescription.logo',
		defaultMessage: 'Bankera logo leads you to homepage',
	},
	apis: {
		id: 'nav.apis',
		defaultMessage: 'API',
	},
	accountApi: {
		id: 'nav.accountApi',
		defaultMessage: 'Account API',
	},
	merchantApi: {
		id: 'nav.merchantApi',
		defaultMessage: 'Merchant API',
	},
	cardsForAds: {
		id: 'nav.cardsForAds',
		defaultMessage: 'Cards for ads',
	},
	partnershipProgram: {
		id: 'nav.partnershipProgram',
		defaultMessage: 'Partnership'
	},
	affiliateProgram: {
		id: 'nav.affiliateProgram',
		defaultMessage: 'Affiliate'
	},
	openBankingApi: {
		id: 'nav.openBankingApi',
		defaultMessage: 'OPEN BANKING API'
	},
	updates: {
		id: 'nav.updates',
		defaultMessage: 'UPDATES'
	},
	securityTitle: {
		id: 'nav.security',
		defaultMessage: 'Security',
	},
	fraudPrevention: {
		id: 'nav.fraudPrevention',
		defaultMessage: 'Fraud prevention',
	},
});

const Footer = () => {
	const { formatMessage, locale } = useIntl();
	const blogLinkLocaleExclusion = ['en', 'et-EE', 'pl-PL', 'el-GR'];
	const menu = {
		solutions: {
			title: formatMessage(translations.solutionsTitle),
			links: [
				{ name: formatMessage(translations.personalTitle), to: routes[locale]['/'] },
				{ name: formatMessage(translations.businessTitle), to: routes[locale]['/business/'] },
				{ name: formatMessage(translations.pricingTitle), to: routes[locale]['/pricing/personal-account/'] },
				{
					name: formatMessage(translations.cardsForAds),
					to: routes[locale]['/solutions/cards-for-online-advertisement/'],
				},
			],
		},
		company: {
			title: formatMessage(translations.companyTitle),
			links: [
				{ name: formatMessage(translations.teamTitle), to: routes[locale]['/team/'] },
				{ name: formatMessage(translations.joinUsTitle), externalLink: 'https://careers.bankera.com' },
				{ name: formatMessage(translations.contactTitle), externalLink: 'https://help.bankera.com/' },
			],
		},
		ecosystem: {
			title: formatMessage(translations.ecosystemTitle),
			links: [
				{ name: 'Spectrocoin', externalLink: 'https://spectrocoin.com/' },
			],
		},
		about: {
			title: formatMessage(translations.aboutTitle),
			links: [
				{ name: formatMessage(translations.legalTitle), to: routes[locale]['/legal/'] },
				{
					name: formatMessage(translations.blogTitle),
					externalLink: blogLinkLocaleExclusion.includes(locale)
						? 'https://blog.bankera.com/'
						: `https://blog.bankera.com/${locale}/`,
				},
				{ name: formatMessage(translations.faqTitle), to: routes[locale]['/faq/'] },
				{ name: formatMessage(translations.updates), to: routes[locale]['/update-schedule/'] },
			],
		},
		security: {
			title: formatMessage(translations.securityTitle),
			links: [
				{
					name: formatMessage(translations.fraudPrevention),
					to: routes[locale]['/fraud-prevention/'],
				},
				{
					name: formatMessage(translations.trustCenter),
					to: routes[locale]['/trust-center/'] || routes.en['/trust-center/'],
				},
			],
		},
		community: {
			title: formatMessage(translations.community),
			links: [
				{ name: formatMessage(translations.bugBounty), to: routes[locale]['/bug-bounty/'] },
				{ name: formatMessage(translations.partnershipProgram), to: routes[locale]['/partnership-program/'] },
				{ name: formatMessage(translations.affiliateProgram), to: routes[locale]['/affiliate-program/'] },
				{ name: formatMessage(translations.events), to: routes[locale]['/events/'] },
			],
		},
		apis: {
			title: formatMessage(translations.apis),
			links: [
				{ name: formatMessage(translations.accountApi), to: '/api-documentation/' },
				{ name: formatMessage(translations.merchantApi), to: '/merchant-api-documentation/' },
				{ name: formatMessage(translations.openBankingApi), to: '/open-banking-api-documentation/' },
			],
		},
	};

	return (
		<footer role="contentinfo">
			<Social language={locale} />
			<div className={style.footerNavigation}>
			<Link
				to={routes[locale]['/']}
				className={classnames(style.logo, { [style.rightMarginForLogo]: locale === Language.Arabic })}
				title={formatMessage(translations.logoImageDescription)}
			>
				{icon.logo}
			</Link>
			<div className={classnames(style.navLinks, { [style.rightMargin]: locale === Language.Arabic })}>
				<div className={style.linkItem}>
					<h2 className={style.heading}>{menu.solutions.title}</h2>
					<ul>
						{menu.solutions.links.map((link: MenuLink) => (
							<li key={link.name}>
								{link.to && (
									<Link
										to={isIE || isEdge ? encodeURI(link.to) : link.to}
										key={link.name}
										className={style.link}
									>
										{link.name}
									</Link>
								)}
							</li>
						))}
					</ul>
				</div>
				<div className={style.linkItem}>
					<h2 className={style.heading}>{menu.company.title}</h2>
					<ul>
						{menu.company.links.map((link: MenuLink) => (
							<li key={link.name} className={style.listItem}>
								{link.to && (
									<Link
										to={isIE || isEdge ? encodeURI(link.to) : link.to}
										key={link.name}
										className={style.link}
									>
										{link.name}
									</Link>
								)}
								{link.externalLink && (
									<a
										href={link.externalLink}
										target="_blank"
										className={style.link}
										rel="noopener noreferrer"
									>
										{link.name}
									</a>
								)}
							</li>
						))}
					</ul>
				</div>
				<div className={style.linkItem}>
					<h2 className={classnames(style.heading)}>{menu.ecosystem.title}</h2>
					<ul>
						{menu.ecosystem.links.map((link: MenuLink) => (
							<li key={link.name}>
								{link.externalLink && (
									<a
										href={link.externalLink}
										target="_blank"
										className={style.link}
										rel="noopener noreferrer"
									>
										{link.name}
									</a>
								)}
							</li>
						))}
					</ul>
				</div>
				<div className={style.linkItem}>
					<h2 className={style.heading}>{menu.community.title}</h2>
					<ul>
						{menu.community.links.map((link: MenuLink) => (
							<li key={link.name}>
								{link.to && (
									<Link
										to={isIE || isEdge ? encodeURI(link.to) : link.to}
										key={link.name}
										className={style.link}
									>
										{link.name}
									</Link>
								)}
								{link.externalLink && (
									<a
										className={style.link}
										href={link.externalLink}
										target="_blank"
										rel="noopener noreferrer"
									>
										{link.name}
									</a>
								)}
							</li>
						))}
					</ul>
				</div>
				<div className={style.linkItem}>
					<h2 className={style.heading}>{menu.about.title}</h2>
					<ul>
						{menu.about.links.map((link: MenuLink) => (
							<li key={link.name}>
								{link.to && (
									<Link
										to={isIE || isEdge ? encodeURI(link.to) : link.to}
										key={link.name}
										className={style.link}
									>
										{link.name}
									</Link>
								)}
								{link.externalLink && (
									<a
										className={style.link}
										href={link.externalLink}
										target="_blank"
										rel="noopener noreferrer"
									>
										{link.name}
									</a>
								)}
							</li>
						))}
					</ul>
				</div>
				<div className={style.linkItem}>
					<h2 className={style.heading}>{menu.security.title}</h2>
					<ul>
						{menu.security.links.map((link: MenuLink) => (
							<li key={link.name}>
								{link.to && (
									<Link
										to={isIE || isEdge ? encodeURI(link.to) : link.to}
										key={link.name}
										className={style.link}
									>
										{link.name}
									</Link>
								)}
							</li>
						))}
					</ul>
				</div>
				<div className={style.linkItem}>
					<h2 className={style.heading}>{menu.apis.title}</h2>
					<ul>
						{menu.apis.links.map((link: MenuLink) => (
							<li key={link.name}>
								{link.to && (
									<Link
										to={isIE || isEdge ? encodeURI(link.to) : link.to}
										key={link.name}
										className={style.link}
									>
										{link.name}
									</Link>
								)}
								{link.externalLink && (
									<a
										className={style.link}
										href={link.externalLink}
										target="_blank"
										rel="noopener noreferrer"
									>
										{link.name}
									</a>
								)}
							</li>
						))}
					</ul>
				</div>
			</div>
			</div>
			<div className={classnames(style.copyright, { [style.rtlAlignment]: locale === Language.Arabic })}>
				<p className={classnames(style.paragraph, { [style.safariParagraph]: isSafari })}>
					<FormattedMessage
						id="copyright.text"
						defaultMessage="All rights reserved © 2017-{year} Bankera"
						values={{
							year: new Date().getFullYear(),
						}}
					/>
				</p>

				<p
					className={classnames(style.paragraph, style.tabletTextRight, {
						[style.safariParagraph]: isSafari,
					})}
				>
					<FormattedMessage
						id="copyright.text2"
						defaultMessage="If you have any questions, please contact us at <a>support@bankera.com</a>"
						values={{
							a: (...chunks: string[]) => <a href="mailto:support@bankera.com">{chunks}</a>,
						}}
					/>
				</p>
			</div>
			<div className={classnames(style.copyrightBottom, { [style.rtlAlignment]: locale === Language.Arabic })}>
				<p className={classnames(style.paragraph, { [style.safariParagraph]: isSafari })}>
					<FormattedMessage
						id="copyright.bottom.text"
						defaultMessage="Era Finance Ltd is a registered agent of Pervesk UAB which is an electronic money institution authorised by the Central Bank of Lithuania (<a>Licence No. 17</a>) for the issuing of electronic money and payment instruments. Era Finance Ltd is not a bank and no banking services are provided on this website."
						values={{
							a: (...chunks: string[]) => (
								<a
									style={{ color: "$gray" }}
									href="https://www.lb.lt/en/enforcement-measures-1/view_license?id=425"
									target="_blank"
									rel="noopener noreferrer"
								>
									{chunks}
								</a>
							),
						}}
					/>
				</p>
			</div>
			<div className={classnames(style.copyrightBottom, { [style.rtlAlignment]: locale === Language.Arabic })}>
				<p className={classnames(style.paragraph, { [style.safariParagraph]: isSafari })}>
					<FormattedMessage
						id="copyright.bottom.text2"
						defaultMessage="Please note that the services described in this website are not offered in the United States and also in any other jurisdiction where such services could be considered restricted. If you choose to access this website, you do so at your own initiative and risk and are responsible for compliance with applicable laws, rules, and regulations that may apply to you."
						values={{
							br: <br />,
						}}
					/>
				</p>
			</div>
		</footer>
	);
};

export default Footer;
